import { QueryProvider } from "@monorepo/query-provider";
import { RouterProvider } from "@monorepo/router-provider";
import { PageLoader } from "@monorepo/loaders";
import { ModalProvider } from "@monorepo/modal";

import type { ModalProviderProps } from "@monorepo/modal";
import type { RouterProviderProps } from "@monorepo/router-provider";
import type { QueryClient, QueryClientConfig } from "@tanstack/react-query";
import type { FC, ReactNode } from "react";
import type { RouteObject } from "react-router-dom";

const getBaseUrl = (): string => {
    if (typeof import.meta !== "undefined") {
        const env = (import.meta as unknown as { env?: { BASE_URL?: string } }).env;

        if (env?.BASE_URL) {
            return env.BASE_URL;
        }
    }

    return "/";
};

type ModalOptions = Omit<ModalProviderProps, "children">;

export interface AppShellProps {
    routes: RouteObject[];
    router?: RouterProviderProps["router"];
    routerFallback?: ReactNode;
    routerSuspenseFallback?: ReactNode;
    routerOptions?: RouterProviderProps["createRouterOptions"];
    queryClient?: QueryClient;
    queryClientConfig?: QueryClientConfig;
    modalOptions?: ModalOptions;
    children?: ReactNode;
}

export const AppShell: FC<AppShellProps> = ({
    routes,
    router,
    routerFallback,
    routerSuspenseFallback,
    routerOptions,
    queryClient,
    queryClientConfig,
    modalOptions = { disableBodyScroll: true },
    children,
}) => (
    <QueryProvider client={queryClient} config={queryClientConfig}>
        <ModalProvider {...modalOptions}>
            <RouterProvider
                routes={routes}
                router={router}
                fallbackElement={routerFallback ?? undefined}
                suspenseFallback={routerSuspenseFallback ?? <PageLoader />}
                createRouterOptions={{ basename: getBaseUrl(), ...routerOptions }}
            />
            {children}
        </ModalProvider>
    </QueryProvider>
);
